import { apiClient } from './api';
import type { AxiosError } from 'axios';
import type { ApiResponse } from '@/types';

export interface Season {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  isArchived: boolean;
  createdAt: string;
}

export interface CreateSeasonRequest {
  name: string;
  startDate: string;
  endDate: string;
  externalSeasonYear?: number;
}

export interface CreateSeasonResponse {
  seasonId: string;
  message: string;
  teamsCreated: number;
  teamsDeactivated: number;
  fixturesCreated: number;
}

export interface TeamStatus {
  id: number;
  name: string;
  code?: string;
  logoUrl?: string;
  isActive: boolean;
}

export interface PickRuleDto {
  id: string;
  seasonId: string;
  half: number;
  maxTimesTeamCanBePicked: number;
  maxTimesOppositionCanBeTargeted: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePickRuleRequest {
  seasonId: string;
  half: number;
  maxTimesTeamCanBePicked: number;
  maxTimesOppositionCanBeTargeted: number;
}

export interface UpdatePickRuleRequest {
  maxTimesTeamCanBePicked: number;
  maxTimesOppositionCanBeTargeted: number;
}

export interface PickRulesResponse {
  firstHalf: PickRuleDto | null;
  secondHalf: PickRuleDto | null;
}

export const adminService = {
  // Season management
  async getSeasons() {
    const response = await apiClient.get<ApiResponse<Season[]>>('/api/v1/admin/seasons');
    return response.data.data!;
  },

  async getActiveSeason() {
    try {
      const response = await apiClient.get<ApiResponse<Season>>('/api/v1/admin/seasons/active');
      return response.data.data ?? null;
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  async createSeason(request: CreateSeasonRequest) {
    const response = await apiClient.post<ApiResponse<CreateSeasonResponse>>(
      '/api/v1/admin/seasons',
      request
    );
    return response.data.data!;
  },

  // Team management
  async getTeamStatuses() {
    const response = await apiClient.get<ApiResponse<TeamStatus[]>>('/api/v1/admin/teams/status');
    return response.data.data!;
  },

  async updateTeamStatus(teamId: number, isActive: boolean) {
    await apiClient.put(`/api/v1/admin/teams/${teamId}/status`, { isActive });
  },

  // Sync operations
  async syncTeams() {
    const response = await apiClient.post<
      ApiResponse<{
        message: string;
        teamsCreated: number;
        teamsUpdated: number;
        totalActiveTeams: number;
      }>
    >('/api/v1/admin/sync/teams');
    return response.data.data!;
  },

  async syncFixtures(season?: number) {
    const url = season
      ? `/api/v1/admin/sync/fixtures?season=${season}`
      : '/api/v1/admin/sync/fixtures';
    const response = await apiClient.post<
      ApiResponse<{
        message: string;
        fixturesCreated: number;
        fixturesUpdated: number;
        gameweeksCreated: number;
      }>
    >(url);
    return response.data.data!;
  },

  async syncResults() {
    const response = await apiClient.post<
      ApiResponse<{
        fixturesUpdated: number;
        gameweeksProcessed: number;
        picksRecalculated: number;
        message: string;
      }>
    >('/api/v1/admin/sync/results');
    return response.data.data!;
  },

  async generateWeeklySchedule() {
    const response = await apiClient.post<
      ApiResponse<{
        success: boolean;
        message: string;
        jobCount: number;
      }>
    >('/api/v1/admin/schedule/generate');
    return response.data.data!;
  },

  // Backfill picks
  async backfillPicks(userId: string, picks: Array<{ gameweekNumber: number; teamId: number }>) {
    const response = await apiClient.post<
      ApiResponse<{
        picksCreated: number;
        picksUpdated: number;
        picksSkipped: number;
        message: string;
      }>
    >('/api/v1/admin/picks/backfill', {
      userId,
      picks,
    });
    return response.data.data!;
  },

  // Pick rules management
  async getPickRules(seasonId: string) {
    const response = await apiClient.get<ApiResponse<PickRulesResponse>>(
      `/api/v1/admin/pick-rules/${encodeURIComponent(seasonId)}`
    );
    return response.data.data!;
  },

  async createPickRule(request: CreatePickRuleRequest) {
    const response = await apiClient.post<ApiResponse<PickRuleDto>>(
      '/api/v1/admin/pick-rules',
      request
    );
    return response.data.data!;
  },

  async updatePickRule(id: string, request: UpdatePickRuleRequest) {
    const response = await apiClient.put<ApiResponse<PickRuleDto>>(
      `/api/v1/admin/pick-rules/${id}`,
      request
    );
    return response.data.data!;
  },

  async deletePickRule(id: string) {
    await apiClient.delete(`/api/v1/admin/pick-rules/${id}`);
  },

  async initializeDefaultPickRules(seasonId: string) {
    const response = await apiClient.post<ApiResponse<PickRulesResponse>>(
      `/api/v1/admin/pick-rules/${encodeURIComponent(seasonId)}/initialize`
    );
    return response.data.data!;
  },

  async enrollAdminForSeason(seasonId: string) {
    // seasonId (the season Name, e.g. "2026/2027") is passed as a query param — a path
    // segment would percent-encode the "/" to %2F which ASP.NET does not decode back.
    await apiClient.post('/api/v1/admin/seasons/enroll-admin', null, {
      params: { seasonId },
    });
  },
};
